/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import {Component, Vue, Watch} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import {
    CommunityChallengeInterface,
    CommunityChallengeMilestoneInterface, CommunityChallengeRewardInterface,
} from '@/store/modules/community-challenge/types';
import {PercentileProgressInterface, PositionStyleInterface} from '@/components/CommunityChallengeStanding/types';
import Popper from 'vue-popperjs';
import {AudioApiCallInterface} from '@/store/modules/game/types';

/**
 * Community Challenge Standing.
 * @author Mark Thompson <mthompson@frontier.co.uk>
 */
@Component({
    name: 'c-community-challenge-standing',
    components: {
        popper: Popper,
    },
})
export default class CommunityChallengeStanding extends Vue {
    @Getter('challenge', {namespace: 'communityChallengeModule'})
    public challenge!: CommunityChallengeInterface;

    @Getter('completionMilestones', {namespace: 'communityChallengeModule'})
    public milestones!: CommunityChallengeMilestoneInterface[];

    @Getter('count', {namespace: 'communityChallengeModule'})
    public count!: number;

    @Action('callAudioApi', {namespace: 'gameModule'})
    public callAudioApi!: (audioApiCall: AudioApiCallInterface) => void;

    public percentiles: PercentileProgressInterface[] = [];
    public percentile: number = 0;

    @Watch('challenge')
    public onChallengeChanged(): void {
        this.percentiles = [];
        this.percentile = 0;
        if (!!this.challenge && !!this.challenge.percentile) {
            Object.keys(this.challenge.percentile).forEach((key: string) => {
                this.percentiles.push({
                    pc: +key,
                    target: +this.challenge.percentile[key],
                    rewards: this.milestones.reduce(
                        (reducer: CommunityChallengeRewardInterface[], item: CommunityChallengeMilestoneInterface) => {
                            return (item.quantity / 100).toFixed(2) === key ?
                                reducer.concat(item.rewards || []) : reducer;
                        }, []),
                });

                if (this.challenge.percentile[key] < (this.count || 0)) {
                    this.percentile = +key;
                }
            });

            if (this.percentiles.length !== 8) {
                this.percentiles = [];
                this.percentile = 0;
            }
        }

    }

    /**
     * @param {CommunityChallengeRewardInterface[]} rewards
     * @return {any}
     */
    public getRewardsInfoText(rewards: CommunityChallengeRewardInterface[]): string {
        return rewards.reduce((reducer: string[], reward: CommunityChallengeRewardInterface) => {
            switch (reward['@type']) {
                case 'RewardCredit':
                    reducer.push(`${reward.amount}CC`);
                    break;
                case 'RewardAnimal':
                    reducer.push((reward.amount || 1) + ' x ' +
                        this.$i18n.translate('achievements.cc.achievements.animal', '') || '');
                    break;
                case 'RewardAvatarShorts':
                    reducer.push(this.$i18n.translate('achievements.cc.achievements.pants', '') || '');
                    break;
                case 'RewardAvatarHat':
                    reducer.push(this.$i18n.translate('achievements.cc.achievements.hat', '') || '');
                    break;
                case 'RewardAvatarShirt':
                    reducer.push(this.$i18n.translate('achievements.cc.achievements.t-shirt', '') || '');
                    break;
            }
            return reducer;
        }, []).join('<br>');
    }

    /**
     * CC rewards checker.
     * @param {CommunityChallengeRewardInterface} reward
     * @return {boolean}
     */
    public isCcReward(reward: CommunityChallengeRewardInterface): boolean {
        return 'RewardCredit' === reward['@type'];
    }

    /**
     * CC rewards checker.
     * @param {CommunityChallengeRewardInterface} reward
     * @return {boolean}
     */
    public isAnimalReward(reward: CommunityChallengeRewardInterface): boolean {
        return 'RewardAnimal' === reward['@type'];
    }

    /**
     * CC rewards checker.
     * @param {CommunityChallengeRewardInterface} reward
     * @return {boolean}
     */
    public isAvatarShirtReward(reward: CommunityChallengeRewardInterface): boolean {
        return 'RewardAvatarShirt' === reward['@type'];
    }

    /**
     * CC rewards checker.
     * @param {CommunityChallengeRewardInterface} reward
     * @return {boolean}
     */
    public isAvatarShortsReward(reward: CommunityChallengeRewardInterface): boolean {
        return 'RewardAvatarShorts' === reward['@type'];
    }

    /**
     * CC rewards checker.
     * @param {CommunityChallengeRewardInterface} reward
     * @return {boolean}
     */
    public isAvatarHatReward(reward: CommunityChallengeRewardInterface): boolean {
        return 'RewardAvatarHat' === reward['@type'];
    }

    /**
     * created lifecycle event.
     */
    public created(): void {
        this.onChallengeChanged();
    }

    /**
     * Get the user icon position based on rating in percentile terms
     * @return {number}
     */
    public get userPosition(): number {
        if (this.percentiles.length === 0 || this.percentile < 1) {
            return 0;
        }

        return Math.floor(((this.percentile - 0.5) * 100) / this.percentiles.length);
    }

    /**
     * Get readable percentage from percentile.
     * @param {number} percentile
     * @return {string}
     */
    public getPecentFromPercentile(percentile: number): string {
        return Math.round((1 - percentile) * 100).toString();
    }

    /**
     * Generate user position inline styles.
     * @return {object}
     */
    public get userPositionStyle(): PositionStyleInterface {
        const style: PositionStyleInterface = {
            left: `${this.userPosition}%`,
        };

        if (this.userPosition > 10) {
            style.transform = 'translateX(-50%)';
        }

        return style;
    }
}
